import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { PatrimonialMutationRowDto } from '@interfaces/partner-income-tax/responses/partner-income-tax.response';
import {
  Tone,
  assetGroupLabel,
  formatRatio,
  formatThousands,
  formatThousandsSigned,
  signTone,
  valueForYear,
} from '@features/dashboard/performance/pages/dfp/components/partner-income-tax/utils/partner-income-tax-format.util';

/** Tipo da linha: grupo da ficha, subtotal/PL (destacados) ou dívidas (queda é boa notícia). */
export type MutationRowKind = 'group' | 'total' | 'debts';

/**
 * Classifica a linha pela chave do grupo ou, na falta dela, pelo rótulo. O backend termina a tabela com
 * "Bens e direitos", "(−) Dívidas e ônus reais" e "Patrimônio líquido".
 */
export function mutationRowKind(
  row: Pick<PatrimonialMutationRowDto, 'group' | 'groupLabel'>
): MutationRowKind {
  const key = `${row.group} ${row.groupLabel}`.toLowerCase();
  if (key.includes('dívida') || key.includes('divida') || key.includes('debt')) return 'debts';
  if (
    key.includes('bens e direitos') ||
    key.includes('patrimônio líquido') ||
    key.includes('patrimonio liquido') ||
    key.includes('total') ||
    key.includes('net-worth')
  ) {
    return 'total';
  }
  return 'group';
}

/** Tom da variação: nas dívidas, cair é positivo. */
export function mutationTone(kind: MutationRowKind, value: number | null): Tone {
  const tone = signTone(value);
  if (kind !== 'debts' || tone === 'neutral') return tone;
  return tone === 'positive' ? 'negative' : 'positive';
}

/** Valor em reais que aparece como zero em R$ mil (ou nulo). */
function isZeroThousands(value: number | null): boolean {
  return value == null || !Number.isFinite(value) || Math.round(value / 1_000) === 0;
}

/** Fração que aparece como zero em percentual com `digits` casas (ou nula). */
function isZeroPercent(ratio: number | null, digits: number): boolean {
  return ratio == null || !Number.isFinite(ratio) || Math.round(ratio * 100 * 10 ** digits) === 0;
}

@Component({
  selector: 'app-pit-patrimonial-mutation-table',
  standalone: true,
  templateUrl: './patrimonial-mutation-table.component.html',
  styleUrl: './patrimonial-mutation-table.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PatrimonialMutationTableComponent {
  readonly rows = input.required<PatrimonialMutationRowDto[]>();
  /** Anos-calendário exibidos, crescente. */
  readonly years = input.required<number[]>();

  protected readonly lastYear = computed(() => {
    const years = this.years();
    return years.length ? years[years.length - 1] : null;
  });

  /**
   * Mesma régua da tabela de bens: grupo sem saldo no ano é "—" (não "0"), e "não mudou" é "—" nas três colunas de
   * variação, em vez de três jeitos diferentes ("—", "0%" e "0,0%").
   */
  protected readonly view = computed(() =>
    this.rows().map(row => {
      const kind = mutationRowKind(row);
      const noChange =
        isZeroThousands(row.lastYearChange) && isZeroPercent(row.lastYearChangePct, 0);
      // Média a.a. sem casa decimal, como o "%" ao lado (revisão com o Bruno, 29/10/2026: "+5%" e "+5,3%" lado a lado
      // pareciam números de régua diferente; para bater o olho, inteiro basta).
      const noTrend = isZeroPercent(row.cagr, 0);
      return {
        row,
        kind,
        // Mesmo nome do grupo na Mutação e em Bens (R4-3): rótulo pelo código do grupo; linhas de total como vieram.
        label: kind === 'group' ? assetGroupLabel(row.group, row.groupLabel) : row.groupLabel,
        values: this.years().map(year => {
          const value = valueForYear(row.values, year);
          return value === 0 ? '—' : formatThousands(value);
        }),
        change: noChange ? '—' : formatThousandsSigned(row.lastYearChange),
        changePct: noChange ? '—' : formatRatio(row.lastYearChangePct, 0, true),
        cagr: noTrend ? '—' : formatRatio(row.cagr, 0, true),
        changeTone: noChange ? 'neutral' : mutationTone(kind, row.lastYearChange),
        cagrTone: noTrend ? 'neutral' : mutationTone(kind, row.cagr),
      };
    })
  );
}
